'use client';

import React, {
  FC,
  ForwardedRef,
  forwardRef,
  InputHTMLAttributes,
  PropsWithChildren,
  useEffect,
  useState,
} from 'react';
import { InferProps } from 'prop-types';
import classNames from 'classnames';
import { FormikHelpers } from 'formik';
import Icon from '@/src/app/components/Icon/Icon';
import { IconName } from '@/src/app/components/Icon/types';
import AppButton from '@/src/app/components/Button/AppButton';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  name: string;
  error?: string;
  success?: string;
  textarea?: boolean;
  requiredLabel?: boolean;
  hideLabel?: boolean;
  description?: string;
  inputClassName?: string;
  isClearable?: boolean;
}

const InputField: FC<InputProps> = forwardRef(
  (
    {
      type = 'text',
      value,
      id,
      name,
      textarea,
      placeholder,
      onChange = () => undefined,
      label,
      error,
      success,
      className,
      required,
      requiredLabel,
      readOnly,
      disabled,
      hideLabel,
      maxLength,
      minLength,
      description,
      autoFocus,
      inputClassName,
      autoComplete,
      step,
      min,
      max,
      isClearable,
      ...props
    }: InferProps<PropsWithChildren<InputProps>> = {
      type: 'text',
      isClearable: false,
    },
    _: ForwardedRef<FormikHelpers<unknown>>,
  ) => {
    const [inputValue, setInputValue] = useState(value);
    let messageStyles = '';
    let inputStyles = '';
    let iconStyles = 'absolute right-4 top-1/2 -translate-y-1/2';
    let iconName: IconName | null = null;

    useEffect(() => {
      setInputValue(value);
    }, [value]);

    if (error) {
      messageStyles += 'text-danger bg-danger-300';
      inputStyles += 'border-danger text-danger pr-12';
      iconStyles += ' text-danger';
      iconName = IconName.ERROR;
    }

    if (success) {
      messageStyles += 'text-success bg-success-300';
      inputStyles += 'border-success text-success pr-12';
      iconStyles += ' text-success';
      iconName = IconName.CHECK_CIRCLE;
    }

    if (isClearable) {
      iconStyles += ' right-12!';

      if (error || success) {
        inputStyles += ' pr-20!';
      }
    }

    function onChangeInput(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
      setInputValue(event.target.value);
      onChange(event);
    }

    return (
      <div className={classNames(className, 'flex flex-col')} {...props}>
        {label ? (
          <label
            htmlFor={id || name}
            className={classNames('label', {
              'sr-only': hideLabel,
            })}
          >
            {label}
            {required || requiredLabel ? '*' : null}
          </label>
        ) : null}
        <div className="relative flex-grow">
          <div className="relative ">
            {textarea ? (
              <textarea
                name={name}
                id={id || name}
                placeholder={placeholder}
                className={classNames(inputStyles, inputClassName || '', 'w-full h-full')}
                onChange={onChangeInput}
                disabled={disabled}
                readOnly={readOnly}
                maxLength={maxLength}
                minLength={minLength}
                value={inputValue}
                autoFocus={autoFocus}
                data-testid="input-field-textarea"
              />
            ) : (
              <>
                {type === 'search' ? (
                  <Icon name={IconName.SEARCH} className="absolute left-4 top-1/2 -translate-y-1/2" />
                ) : null}
                <input
                  type={type}
                  value={inputValue}
                  id={id || name}
                  placeholder={placeholder}
                  name={name}
                  onChange={onChangeInput}
                  onBlur={() => (type !== 'number' ? setInputValue(inputValue.trim()) : null)}
                  minLength={minLength}
                  maxLength={maxLength}
                  className={classNames(inputStyles, inputClassName || '', 'w-full', {
                    'pl-12': ['search'].includes(type),
                    '[&::-webkit-search-cancel-button]:hidden pr-12': isClearable,
                  })}
                  disabled={disabled}
                  readOnly={readOnly}
                  autoFocus={autoFocus}
                  autoComplete={autoComplete}
                  step={step}
                  min={min}
                  max={max}
                  data-testid="input-field-input"
                />
                {isClearable && inputValue.length ? (
                  <AppButton
                    onClick={() => {
                      setInputValue('');
                      onChangeInput({ target: { value: '', name } } as React.ChangeEvent<HTMLInputElement>);
                    }}
                    type="button"
                    text
                    className="absolute right-3 top-1/2 -translate-y-1/2 "
                  >
                    <Icon name={IconName.CLOSE} className="text-base!" />
                  </AppButton>
                ) : null}
              </>
            )}
            {iconName ? <Icon name={iconName} className={classNames(iconStyles)} /> : null}
          </div>
        </div>

        {description && !(error || success) ? (
          <div className="text-xs inline-block mt-1 px-2 text-gray-500">{description}</div>
        ) : null}
        {error || success ? (
          <div className="mt-2">
            <span
              className={classNames('text-xs', 'inline-block', 'py-1', 'px-2', messageStyles)}
              dangerouslySetInnerHTML={{ __html: error || success }}
              data-testid="inline-input-notification"
            />
          </div>
        ) : null}
      </div>
    );
  },
);
InputField.displayName = 'InputField';

export default InputField;
